import { scrollMainContentIntoView } from '../utils/scrollUtils';
import { addMessage, addMessageAndSetRendered, handleApiError } from './messagesActions';
import { deleteField } from './odooFieldActions';
import { getUserDetails } from './userActions';
import { FORM_ERROR } from 'final-form';
import request from 'superagent';

// Start saving a given form
export const saveOdooForm = (formType, formId, formPage) => {
  return {
    type: 'ODOOFORM_SAVE_START',
    formType,
    formId,
    formPage,
  };
};

// Complete fetching a given form
export const setOdooForm = (formType, formId, formPage, form) => {
  return {
    type: 'ODOOFORM_SET_FORM',
    formType,
    formId,
    formPage,
    form,
  };
};

// Add a new form of a given type
// Sends an API call, and receives a form page description in return.
// formTypeId is the 'id' parameter of the formType to create, eg REG002
// history can be fetched from this.props
export const createOdooForm = (sessionId, formTypeId, history, callback) => {
  return (dispatch) => {
    dispatch({
      type: 'FORMTYPE_CREATE_START',
      formId: formTypeId,
    });
    return request
      .get('registry/api/v1/form_add/' + formTypeId)
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          dispatch({
            type: 'FORMTYPE_CREATE_END',
          });
          try {
            let out = JSON.parse(res.text);

            // Clear field values that may already be in local storage from the last time this form was used.
            out.page.sections.forEach((section) => {
              section.fieldGroups.forEach((fieldGroup) => {
                fieldGroup.fields.forEach((field) => {
                  dispatch(deleteField(field.fieldName));
                });
              });
            });

            dispatch(addMessage(out.message, 'success'));
            callback(out.form.code, out.form.formToken);
          } catch (err) {
            dispatch(handleApiError(err));
            callback();
          }
        },
        // failure promise
        (err) => {
          dispatch({
            type: 'FORMTYPE_CREATE_END',
          });
          dispatch(handleApiError(err));
          callback();
        },
      );
  };
};

// Delete a given form
export const removeOdooForm = (sessionId, formType, formId) => {
  return (dispatch) => {
    dispatch({
      type: 'FORMTYPE_DELETE_START',
    });
    return request
      .del('registry/api/v1/form/' + formType + '/' + formId) // use DELETE HTTP method
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          try {
            let out = JSON.parse(res.text);
            dispatch(addMessageAndSetRendered(out.message, 'success'));
          } catch (err) {
            dispatch(handleApiError(err));
          }
          // Re-fetch user details which should contain the forms list minus the form we just deleted
          // User details also marks the forms as deletable again.
          dispatch(getUserDetails(sessionId));
        },
        // failure promise
        (err) => {
          dispatch(handleApiError(err));
          dispatch({
            type: 'FORMTYPE_DELETE_END',
          });
        },
      );
  };
};

// Async action to trigger the load/set cycle
export const fetchOdooForm = (sessionId, formType, formId, formPage) => {
  return (dispatch) => {
    // Show loading screen
    dispatch({
      type: 'ODOOFORM_LOAD_START',
      formType,
      formId,
      formPage,
    });

    // API endpoint uses form ID with optional page code
    let url = '/registry/api/v1/form/' + formType + '/' + formId;
    if (formPage) {
      url += '/' + formPage;
    }

    request
      .get(url)
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          try {
            const out = JSON.parse(res.text);

            // Check whether we can access this page
            let allowed = false;
            for (let i = 0, ii = out.form.pageCodes.length; i < ii; i++) {
              if (out.page.code === out.form.pageCodes[i].code && out.form.pageCodes[i].pageState === 'enabled') {
                allowed = true;
                break;
              }
            }
            // If our page isn't in the list of allowed pageCodes,
            // throw an error which results in a 404 page being displayed.
            if (!allowed) {
              throw new Error();
            }

            dispatch(addMessage(out.message, 'success'));

            // Create a map of which fields trigger changes to other fields
            // The data from the API points backwards to what we actually need.
            if (typeof out.page.sections !== 'undefined') {
              // Create the map of triggerField => dependantFields
              let dependencies = {};
              for (let i = 0, ii = out.page.sections.length; i < ii; i++) {
                for (let j = 0, jj = out.page.sections[i].fieldGroups.length; j < jj; j++) {
                  for (let k = 0, kk = out.page.sections[i].fieldGroups[j].fields.length; k < kk; k++) {
                    let field = out.page.sections[i].fieldGroups[j].fields[k];
                    if (typeof field.fieldDetails === 'undefined' || field.fieldDetails === null) {
                      continue;
                    }
                    if (typeof field.fieldDetails.dependsOn === 'undefined') {
                      continue;
                    }
                    for (let m = 0, mm = field.fieldDetails.dependsOn.length; m < mm; m++) {
                      // Add a new 'dependencies' list to each dependant field pointing back at this one,
                      // so changes to that field can trigger events on the dependant fields
                      if (typeof dependencies[field.fieldDetails.dependsOn[m].fieldName] === 'undefined') {
                        dependencies[field.fieldDetails.dependsOn[m].fieldName] = [];
                      }
                      dependencies[field.fieldDetails.dependsOn[m].fieldName].push({
                        fieldName: field.fieldName,
                        fieldGroupName: out.page.sections[i].fieldGroups[j].name,
                        isHasMany: out.page.sections[i].fieldGroups[j].hasMany,
                        endpoint: field.fieldDetails.endpoint,
                        dependsOn: field.fieldDetails.dependsOn,
                      });
                    }
                  }
                }
              }
              // Add map data to form spec as new 'dependencies' property for each field
              // that can trigger changes on other fields
              for (let i = 0, ii = out.page.sections.length; i < ii; i++) {
                for (let j = 0, jj = out.page.sections[i].fieldGroups.length; j < jj; j++) {
                  for (let k = 0, kk = out.page.sections[i].fieldGroups[j].fields.length; k < kk; k++) {
                    let field = out.page.sections[i].fieldGroups[j].fields[k];
                    if (typeof dependencies[field.fieldName] !== 'undefined') {
                      out.page.sections[i].fieldGroups[j].fields[k].dependantFields = dependencies[field.fieldName];
                    }
                  }
                }
              }
            }

            // Set our new form data
            dispatch(setOdooForm(out.form.code, out.form.formToken, out.page.code, out));
            scrollMainContentIntoView();
          } catch (err) {
            dispatch(handleApiError(err));
          }
          dispatch({
            type: 'ODOOFORM_LOAD_END',
          });
        },
        // failure promise
        (err) => {
          dispatch(handleApiError(err));
          dispatch({
            type: 'ODOOFORM_LOAD_END',
          });
        },
      );
  };
};

// callback will be run just prior to calling history.push if given
export const submitOdooForm = (formData, history) => {
  return (dispatch, getState) => {
    // Set the new form's details into redux
    dispatch(saveOdooForm(formData.formType, formData.formId, formData.formPage));

    return request
      .post('/registry/api/v1/form/' + formData.formType + '/' + formData.formId + '/' + formData.formPage)
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .send({
        page: JSON.stringify(formData),
      })
      .timeout({
        response: 600000,
        deadline: 600000,
      })
      .then(
        // success promise
        (res) => {
          let errors = {},
            hasValidationErrors = false;
          try {
            dispatch({ type: 'ODOOFORM_SAVE_END' });

            let out = JSON.parse(res.text);

            // Was the save successful? Having a "page" section in the response indicates errors.
            if (typeof out.page === 'undefined' || out.page.state === 'done') {
              // Add success message
              dispatch(addMessage(out.message, 'success'));

              const loadNextPage = () => {
                // If the page we just submitted is a payment page, redirect to DPS
                const submittedForm = getState().odooForm.form;
                const odooUserId = getState().user && getState().user.userId ? getState().user.userId : null;
                const isPaymentPage = typeof submittedForm.page.payment !== 'undefined';
                if (isPaymentPage) {
                  let payment = submittedForm.page.payment;
                  // Create the URLs we want to redirect back to after the payment gateway
                  // by using the URL up to "/form/" and adding the form type, form ID, and next page code
                  let returnUrl =
                    window.location.href.match(/(^.*\/form\/)/)[0] +
                    submittedForm.form.code +
                    '/' +
                    submittedForm.form.formToken +
                    '/' +
                    payment.nextPage;
                  // Default to the normal return URL if we haven't set an error return page
                  let returnErrorUrl =
                    window.location.href.match(/(^.*\/form\/)/)[0] +
                    submittedForm.form.code +
                    '/' +
                    submittedForm.form.formToken +
                    '/' +
                    (typeof payment.nextPageError !== 'undefined' && payment.nextPageError ? payment.nextPageError : payment.nextPage);
                  // Create URL to do the payment gateway round trip
                  if (!payment.transactionId) {
                    console.error('Payment page missing transactionId');
                    return;
                  }
                  let redirectUrl =
                    '/external-payments/payment?' +
                    'amount=' +
                    encodeURIComponent(payment.amount) +
                    '&' +
                    'transactionId=' +
                    encodeURIComponent(payment.transactionId) +
                    '&' +
                    'returnUrl=' +
                    encodeURIComponent(returnUrl) +
                    '&' +
                    'returnErrorUrl=' +
                    encodeURIComponent(returnErrorUrl);
                  // Include any extra payment details (e.g. Odoo user id) as query parameters
                  if (odooUserId) {
                    payment.details = {
                      ...payment.details,
                      odooUserId,
                    };
                  }
                  for (let key in payment.details) {
                    redirectUrl += '&' + key + '=' + encodeURIComponent(payment.details[key]);
                  }
                  // Redirect to the new location
                  window.location = redirectUrl;
                  return;
                }

                // Save was successful - find the page after the one we just submitted that isn't pageState='disabled', and load it
                // Note that the pageCodes and disabled states may change after submitting, so we need to use the list from the
                // response we just got.
                // Default to loading the last page, in case we just managed to submit the last page,
                // which should never happen...
                let foundCurrentPage = false;
                let nextPageCode = out.form.pageCodes[out.form.pageCodes.length - 1].code;
                for (let i = 0, ii = out.form.pageCodes.length; i < ii; i++) {
                  if (out.form.pageCodes[i].code === formData.formPage && i + 1 < ii) {
                    // skip over the current page
                    foundCurrentPage = true;
                    continue;
                  }
                  if (!foundCurrentPage) {
                    // skip over pages that are before the current one
                    continue;
                  }
                  if (out.form.pageCodes[i].pageState === 'disabled') {
                    // skip pages that are disabled - note we're looking at the data just returned, not
                    // the data we already had, as the disabled state may have changed depending on what we
                    // have submitted
                    continue;
                  }
                  // We have found our next page.
                  nextPageCode = out.form.pageCodes[i].code;
                  break;
                }

                // Push new URL, which will trigger loading the next page
                history.push('/form/' + out.form.code + '/' + out.form.formToken + '/' + nextPageCode);
              };

              loadNextPage();
              return;
            }

            // Add error message
            dispatch(handleApiError(out));

            // Tell our page we're done saving
            dispatch({
              type: 'ODOOFORM_SAVE_END',
            });

            // Checks if the page contains validation errors
            if (typeof out.page !== 'undefined' && out.page.state === 'error') {
              if (typeof out.page.sections !== 'undefined') {
                // Check all fields for state === 'error' and add them to the error object
                out.page.sections.forEach((section) => {
                  section.fieldGroups.forEach((fieldGroup) => {
                    fieldGroup.fields.forEach((field) => {
                      // hasMany field groups need to assign errors from an array
                      if (fieldGroup.hasMany === true) {
                        if (typeof field.state !== 'undefined' && field.state !== null && typeof field.state.length !== 'undefined') {
                          for (let i = 0, ii = field.state.length; i < ii; i++) {
                            if (field.state[i] === 'error') {
                              // Check that the required errors structure exists, create it if not
                              if (typeof errors[fieldGroup.name] === 'undefined') {
                                errors[fieldGroup.name] = [];
                              }
                              if (typeof errors[fieldGroup.name][i] === 'undefined') {
                                errors[fieldGroup.name][i] = {};
                              }
                              // Add the error messages
                              hasValidationErrors = true;
                              errors[fieldGroup.name][i][field.fieldName] = field.message[i];
                            }
                          }
                        }
                        return;
                      }
                      // non-hasMany field groups are just strings
                      if (field.state === 'error') {
                        hasValidationErrors = true;
                        errors[field.fieldName] = field.message;
                      }
                    });
                  });
                });
              }
            }

            // Add error if practitioner does not provide an explanation or attachment
            // for "No" answers in a questionnaire
            if (out.message === 'Please provide an explanation or attachment for your "No" answer') {
              // formData contains the submitted form values with field names as keys
              // Set errors on all fields with value '17' (No)
              for (let field in formData) {
                // Skip non-field properties like formType, formId, formPage, sessionId
                if (['formType', 'formId', 'formPage', 'sessionId'].includes(field)) {
                  continue;
                }
                if (formData[field] === '17') {
                  // "No" value
                  errors[field] = 'Please provide an explanation or attachment below';
                }
              }
              hasValidationErrors = true;
            }
          } catch (err) {
            dispatch(handleApiError(err));
          }

          // Return errors to show any validation errors on the form
          if (hasValidationErrors) {
            errors['_error'] = 'Submission Failed';
            return errors;
          }
        },
        // failure promise
        (err) => {
          dispatch(handleApiError(err));
        },
      );
  };
};

// Special case of fetchOdooForm where we need to fetch the registration form structure
// from a different API without a session
// The formId parameter should be fetched from the config set on the application by SilverStripe.
export const fetchRegistrationForm = (formId, formPage) => {
  return (dispatch) => {
    // Show loading screen
    dispatch({
      type: 'ODOOFORM_LOAD_START',
      formId: null,
      formPage: null,
    });

    let url = 'registry/api/v1/apply';
    if (formId !== null) {
      url += '/' + formId;
    }
    if (formPage !== null) {
      url += '/' + formPage;
    }

    request
      .get(url)
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          try {
            let out = JSON.parse(res.text);
            dispatch(addMessage(out.message, 'success'));
            // Set our new form data
            dispatch(setOdooForm(out.form.code, out.form.formToken, out.page.code, out));
            dispatch({
              type: 'ODOOFORM_LOAD_END',
            });
          } catch (err) {
            dispatch(handleApiError(err));
          }
        },
        // failure promise
        (err) => {
          dispatch(handleApiError(err));
        },
      );
  };
};

export const submitRegistrationForm = (config, formData, history) => {
  return async (dispatch) => {
    // Set the new form's details into redux
    dispatch(saveOdooForm(formData.formType, formData.formId, formData.formPage));

    try {
      const res = await request
        .post('/registry/api/v1/apply/' + formData.formId + '/' + formData.formPage)
        .type('form') // otherwise sent as JSON and not understood by Odoo
        .send({ page: JSON.stringify(formData) })
        .timeout({ response: 60000, deadline: 90000 });

      const out = JSON.parse(res.text);
      dispatch(addMessageAndSetRendered(out.message, 'success'));

      // Was the save successful? Having a "page" section in the response indicates errors.
      if (typeof out.page === 'undefined' || out.page.state === 'done') {
        // Save was successful - find the page after the one we just submitted, and load it
        let nextPageCode = out.form.pageCodes[out.form.pageCodes.length - 1].code;
        for (let i = 0, ii = out.form.pageCodes.length; i < ii; i++) {
          if (out.form.pageCodes[i].code === formData.formPage && i + 1 < ii) {
            nextPageCode = out.form.pageCodes[i + 1].code;
          }
        }
        history.push('/register/' + out.form.formToken + '/' + nextPageCode);
        return;
      }

      // Set our new form data including
      dispatch(setOdooForm(out.form.code, out.form.formToken, out.page.code, out));

      // Tell our page we're done saving
      dispatch({ type: 'ODOOFORM_SAVE_END' });

      // Handle validation errors from the response
      const errors = {};
      let hasValidationErrors = false;

      if (out.page?.state === 'error' && Array.isArray(out.page.sections)) {
        out.page.sections.forEach((section) => {
          section.fieldGroups.forEach((fieldGroup) => {
            fieldGroup.fields.forEach((field) => {
              if (field.state === 'error') {
                hasValidationErrors = true;
                errors[field.fieldName] = field.message;
              }
            });
          });
        });
      }

      if (hasValidationErrors) {
        return {
          ...errors,
          [FORM_ERROR]: 'Submission Failed',
        };
      }
    } catch (err) {
      dispatch(handleApiError(err));
    }
  };
};

// Mark a field as needing field details updating.
//
// This will update its' props via redux, and FormField.componentDidUpdate will then
// trigger updateFieldDetails. FormField.render() will show a loading spinner while
// the API call is made.
//
// We don't trigger updateFieldDetails directly, as the form value that
// triggered the change won't have made it into redux yet.
export const markFieldDetailsDirty = (fieldName, fieldGroupName, hasManyRow) => {
  return {
    type: 'ODOOFORM_MARK_FIELDDETAILS_DIRTY',
    fieldName,
    fieldGroupName,
    hasManyRow,
  };
};

// Fetches new field details from an API using a list of fields whose values we should send to the API,
// and set the resulting fieldDetails on the target field (dependency)
//
// {
//  "fieldName":"app_scope_qualification",
//  "fieldGroupName":"APP - Scope - Qualifications (Darren)",
//  "isHasMany":true,
//  "rowIndex":0,
//  "endpoint":"/registry/api/v1/lookup/qualification",
//  "dependsOn":[
//    "app_scope_institution",
//    "app_scopes_scope"
//  ]
// }
export const updateFieldDetails = (fieldName, fieldGroupName, rowIndex, dependsOn, endpoint, currentFormValues = null) => {
  return (dispatch, getState) => {
    const state = getState();
    const { odooForm } = state;
    const formValues = currentFormValues || state.form?.odooForm?.values;

    if (!formValues) {
      return;
    }

    // add form data
    let formData = {
      formToken: odooForm.formId,
      formCode: odooForm.formType,
      page: odooForm.formPage,
    };

    // Find dependency values to send to API, add them to formData as fieldName=value
    for (let i = 0, ii = dependsOn.length; i < ii; i++) {
      let dependency = dependsOn[i];
      formData[dependency.fieldName] = '';
      // If we have a value, add it to the form data to post.
      // If we do not have a value, we don't have enough data to make this API call
      // yet, so we just return.
      if (dependency.isHasMany) {
        if (typeof formValues[dependency.fieldGroupName] === 'undefined') {
          continue;
        }
        if (typeof formValues[dependency.fieldGroupName][rowIndex] === 'undefined') {
          continue;
        }
        if (typeof formValues[dependency.fieldGroupName][rowIndex][dependency.fieldName] === 'undefined') {
          continue;
        }
        formData[dependency.fieldName] = formValues[dependency.fieldGroupName][rowIndex][dependency.fieldName];
      } else {
        if (typeof formValues[dependency.fieldName] === 'undefined') {
          continue;
        }
        formData[dependency.fieldName] = formValues[dependency.fieldName];
      }
    }

    // If we don't have enough data yet, set the field to be not dirty and empty
    for (let i = 0, ii = dependsOn.length; i < ii; i++) {
      let dependency = dependsOn[i];
      if (
        !formData[dependency.fieldName] ||
        (typeof formData[dependency.fieldName].length !== 'undefined' && formData[dependency.fieldName].length === 0)
      ) {
        dispatch({
          type: 'ODOOFORM_SET_FIELD_DETAILS',
          hasManyGroup: fieldGroupName,
          rowIndex: rowIndex,
          fieldName: fieldName,
          fieldDetails: [],
        });
        return;
      }
    }

    dispatch({
      type: 'ODOOFORM_MARK_FIELDDETAILS_UPDATING',
      fieldName: fieldName,
      fieldGroupName: fieldGroupName,
      hasManyRow: rowIndex,
    });

    return request
      .get(endpoint)
      .type('form') // otherwise sent as JSON and not understood by Odoo
      .query(formData)
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          let out = [];
          try {
            // Set new fieldDetails
            out = JSON.parse(res.text);
          } catch (err) {
            dispatch(handleApiError(err));
          }
          dispatch({
            type: 'ODOOFORM_SET_FIELD_DETAILS',
            hasManyGroup: fieldGroupName,
            rowIndex: rowIndex,
            fieldName: fieldName,
            fieldDetails: out,
          });
        },
        // failure promise
        (err) => {
          dispatch(handleApiError(err));
          dispatch({
            type: 'ODOOFORM_SET_FIELD_DETAILS',
            hasManyGroup: fieldGroupName,
            rowIndex: rowIndex,
            fieldName: fieldName,
            fieldDetails: [],
          });
        },
      );
  };
};

export const searchPeers = (sessionId, form) => {
  return (dispatch) => {
    dispatch({ type: 'SEARCH_PEERS_START' });
    const params = Object.entries(form).map(([key, value]) => `${key}=${encodeURIComponent(value)}`);
    return request
      .get(`registry/api/v1/search?${params.join('&')}`)
      .timeout({
        response: 60000,
        deadline: 90000,
      })
      .then(
        // success promise
        (res) => {
          let out = JSON.parse(res.text);
          dispatch({ type: 'SEARCH_PEERS', peers: out });
        },
        // failure promise
        (err) => {
          dispatch({ type: 'SEARCH_PEERS_ERROR' });
          dispatch(handleApiError(err));
        },
      );
  };
};
